import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isBlank } from '@/shared/lib'
import { Spinner } from '@/shared/ui/Spinner'
import s from './StatusBadge.module.css'

/**
 * Смысловой тон состояния.
 * `success` — сделано и доступно, `warning` — ожидание, `danger` — проблема,
 * `accent` — процесс идёт, `neutral` — нейтральное и неизвестное.
 */
export type StatusBadgeTone = 'success' | 'warning' | 'danger' | 'accent' | 'neutral'

/**
 * Плотность подачи. `tint` — рабочий вариант списков и форм,
 * `solid` — единичный акцентный статус на экране, `quiet` — тон нужен, шум не нужен.
 */
export type StatusBadgeVariant = 'tint' | 'solid' | 'quiet'

/** Собственная шкала бейджа: xs 24 (грид, карточка), sm 28 (основной), md 34 (шапка, статус связи). */
export type StatusBadgeSize = 'xs' | 'sm' | 'md'

/**
 * Маркер слева. Точка — состояние, пульсирующая точка — живой канал (связь с 1С),
 * спиннер — идущий процесс. Это три разных сигнала, поэтому одна ось, а не три флага.
 */
export type StatusBadgeIndicator = 'none' | 'dot' | 'pulse' | 'spinner'

export type StatusBadgeProps = {
  /** Текст статуса. Приходит с бэкенда готовым; пустое значение не рисуется вовсе. */
  children: ReactNode
  tone?: StatusBadgeTone
  variant?: StatusBadgeVariant
  size?: StatusBadgeSize
  indicator?: StatusBadgeIndicator
  /** Слот вместо маркера: пин «есть рядом», замок, скрепка. Перекрывает `indicator`. */
  icon?: ReactNode
  /** Моноширинный набор идентификаторов: номер накладной, ИНН, ETD. */
  mono?: boolean
  /**
   * Полная фраза для ассистивных технологий («Статус заказа 45-ЭЛ/2026: просрочен на 6 дней»).
   * Задана — видимый текст уходит под `aria-hidden`, вместо него произносится фраза.
   * Не задана — статус читается как есть, в контексте строки или карточки.
   */
  label?: string
  ref?: Ref<HTMLSpanElement>
} & Omit<HTMLAttributes<HTMLSpanElement>, 'children'>

// Карта классов при noUncheckedIndexedAccess приходит как string | undefined
const TONE_CLASS: Record<StatusBadgeTone, string | undefined> = {
  success: s.toneSuccess,
  warning: s.toneWarning,
  danger: s.toneDanger,
  accent: s.toneAccent,
  neutral: s.toneNeutral,
}

const VARIANT_CLASS: Record<StatusBadgeVariant, string | undefined> = {
  tint: s.tint,
  solid: s.solid,
  quiet: s.quiet,
}

const SIZE_CLASS: Record<StatusBadgeSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
  md: s.md,
}

/**
 * Статус объекта, названный словом: «В наличии», «На согласовании», «Просрочен 6 дн».
 *
 * Смысл несёт текст, а не цвет: в ч/б печати, при дальтонизме и в режиме высокой
 * контрастности от капсулы остаётся только подпись — и её достаточно.
 * Число без слова — это CounterBadge, нажимаемый фильтр — Chip.
 *
 * Контракт: docs/ui-kit/contracts/status-badge.md
 */
export function StatusBadge({
  children,
  tone = 'neutral',
  variant = 'tint',
  size = 'sm',
  indicator = 'dot',
  icon,
  mono = false,
  label,
  className,
  ref,
  ...rest
}: StatusBadgeProps) {
  // Пустая цветная капсула в ячейке — визуальный мусор; прочерк рисует потребитель
  if (isBlank(children)) return null

  const announced = Boolean(label)
  const showDot = !icon && (indicator === 'dot' || indicator === 'pulse')
  const showSpinner = !icon && indicator === 'spinner'

  return (
    <span
      {...rest}
      ref={ref}
      className={cx(
        s.root,
        SIZE_CLASS[size],
        TONE_CLASS[tone],
        VARIANT_CLASS[variant],
        mono && s.mono,
        className,
      )}
      data-tone={tone}
      data-variant={variant}
      data-size={size}
    >
      {/* слот декоративен: смысл несёт текст статуса, иначе имя удвоится рядом с label */}
      {icon ? (
        <span className={s.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {showDot ? <span className={cx(s.dot, indicator === 'pulse' && s.dotPulse)} /> : null}
      {/* Спиннер декоративен: о процессе уже сказано словом в тексте бейджа */}
      {showSpinner ? <Spinner size="xs" tone="current" /> : null}

      <span className={s.text} aria-hidden={announced || undefined}>
        {children}
      </span>
      {announced ? <span className={s.label}>{label}</span> : null}
    </span>
  )
}
